import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import { poisEndpoints } from '@ooh/contracts';
import type { CreatePoiBody, PoiImportTriggerResponse } from '@ooh/contracts';
import { POI_LIST_KEY_PREFIX, poiQueries } from './queries';
import type { WithCallWithAuth } from './queries';

export function useEffectiveAuth(auth?: WithCallWithAuth): WithCallWithAuth {
  const routeContext = useRouteContext({ from: '__root__' });
  return auth ?? (routeContext as { auth: WithCallWithAuth }).auth;
}

// ─── Create POI ───────────────────────────────────────────────

export function useCreatePoi(auth?: WithCallWithAuth) {
  const effectiveAuth = useEffectiveAuth(auth);
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, CreatePoiBody>({
    mutationFn: (body) => effectiveAuth.callWithAuth(poisEndpoints.create, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: POI_LIST_KEY_PREFIX });
    },
  });
}

// ─── Soft delete POI ──────────────────────────────────────────

export function useSoftDeletePoi(auth?: WithCallWithAuth) {
  const effectiveAuth = useEffectiveAuth(auth);
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, { id: string }>({
    mutationFn: ({ id }) => effectiveAuth.callWithAuth(poisEndpoints.delete, { id }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: POI_LIST_KEY_PREFIX });
    },
    // Suppress global MutationCache.onError toast — error handling is done at the call site
    // via mutateAsync try/catch in DeletePoiDialog. Without this, MutationCache.onError fires
    // a duplicate toast because it only checks mutation.options.onError (not per-call callbacks).
    onError: () => {},
  });
}

// ─── Toggle POI internal_only ────────────────────────────────

export function useTogglePoiInternal(auth?: WithCallWithAuth) {
  const effectiveAuth = useEffectiveAuth(auth);
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, { id: string; internal_only: boolean }>({
    mutationFn: ({ id, internal_only }) =>
      effectiveAuth.callWithAuth(poisEndpoints.patch, { id, internal_only }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: POI_LIST_KEY_PREFIX });
    },
  });
}

// ─── Trigger POI import ───────────────────────────────────────

export function useTriggerPoiImport(auth?: WithCallWithAuth) {
  const effectiveAuth = useEffectiveAuth(auth);
  const queryClient = useQueryClient();

  return useMutation<PoiImportTriggerResponse, Error, void>({
    mutationFn: () => effectiveAuth.callWithAuth(poisEndpoints.triggerImport, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: poiQueries.importStatus(effectiveAuth).queryKey,
      });
    },
  });
}
